import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { getColor, getSpacing, getShadow, DesignSystem } from '../styles/designSystem';
import { FadeInUpContainer } from '../styles/animations';
import { API_BASE_URL } from '../api/tourism';

// Keyframes for lightbox
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

// Types
interface MediaItem {
  type: 'image' | 'video';
  url: string;
  thumbnail_url?: string;
}

interface Review {
  id: string;
  customer_name: string;
  customer_image?: string;
  rating: number;
  title: string;
  review_text: string;
  location: string;
  trip_date: string;
  media_items: MediaItem[];
  verified_booking: boolean;
  created_at: string;
  likes: number;
}

// Fisher-Yates shuffle — returns a new shuffled copy
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ==============================================
// OPTION 1: CLEAN GRID WITH ARROW NAVIGATION
// 5 reviews per row, clean design with lightbox
// ==============================================

const Option1Container = styled.section`
  padding: 48px 0;
  background: linear-gradient(
    180deg,
    ${getColor('neutral.white')} 0%,
    ${getColor('primary.50')} 50%,
    ${getColor('neutral.white')} 100%
  );
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 24px 0;
  }
  
  @media (max-width: 480px) {
    padding: 16px 0;
  }
`;

const CarouselWrapper = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${getSpacing('md')};
  position: relative;
  
  @media (min-width: 768px) {
    padding: 0 ${getSpacing('lg')};
  }
  
  @media (min-width: 1024px) {
    padding: 0 ${getSpacing('xl')};
  }
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${getSpacing('lg')};
  margin: ${getSpacing('2xl')} 0;
  position: relative;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`;

const ReviewCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  overflow: hidden;
  box-shadow: ${getShadow('soft')};
  transition: all ${DesignSystem.motion.duration.normal} ${DesignSystem.motion.easing.ease};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${getShadow('luxe')};
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: cover;
  transition: transform ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.ease};
  
  ${ReviewCard}:hover & {
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  padding: ${getSpacing('md')} ${getSpacing('lg')} ${getSpacing('lg')};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ReviewerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('md')};
  margin-bottom: ${getSpacing('md')};
`;

const Avatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: ${DesignSystem.borderRadius.full};
  object-fit: cover;
  border: 2px solid ${getColor('accent.600')};
`;

const DefaultAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${DesignSystem.borderRadius.full};
  background: linear-gradient(135deg, ${getColor('accent.500')}, ${getColor('secondary.500')});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
`;

const ReviewerInfo = styled.div`
  flex: 1;
`;

const ReviewerName = styled.h4`
  margin: 0;
  color: ${getColor('primary.900')};
  font-size: 1rem;
  font-weight: 600;
`;

const StarsContainer = styled.div`
  display: flex;
  gap: 3px;
  margin-top: ${getSpacing('xs')};
`;

// Gradient SVG Star Component
const GradientStar: React.FC<{ filled: boolean; index: number }> = ({ filled, index }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id={`starGrad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#CAB96E" />
        <stop offset="100%" stopColor="#D4C77F" />
      </linearGradient>
    </defs>
    <path
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      fill={filled ? `url(#starGrad-${index})` : 'none'}
      stroke={filled ? '#CAB96E' : getColor('neutral.gray.300')}
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const ReviewTitle = styled.h3`
  margin: ${getSpacing('sm')} 0 ${getSpacing('xs')};
  color: ${getColor('primary.900')};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReviewText = styled.p`
  color: ${getColor('neutral.gray.700')};
  font-size: 0.82rem;
  line-height: 1.6;
  margin: 0;
  flex: 1;
`;

// ===== LIGHTBOX COMPONENTS =====

const LightboxOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: ${DesignSystem.zIndex.modal};
  padding: ${getSpacing('xl')};
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: opacity ${DesignSystem.motion.duration.normal} ease;
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
`;

const LightboxCard = styled.div`
  background: ${getColor('neutral.white')};
  border-radius: ${DesignSystem.borderRadius['2xl']};
  max-width: 640px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${getShadow('luxe')}, 0 0 80px rgba(0, 0, 0, 0.4);
  animation: ${fadeInScale} ${DesignSystem.motion.duration.slow} cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
`;

const LightboxImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
  border-radius: ${DesignSystem.borderRadius['2xl']} ${DesignSystem.borderRadius['2xl']} 0 0;
`;

const LightboxContent = styled.div`
  padding: ${getSpacing('xl')} ${getSpacing('2xl')} ${getSpacing('2xl')};
`;

const LightboxReviewerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('md')};
  margin-bottom: ${getSpacing('lg')};
`;

const LightboxAvatar = styled.img`
  width: 56px;
  height: 56px;
  border-radius: ${DesignSystem.borderRadius.full};
  object-fit: cover;
  border: 2px solid ${getColor('accent.600')};
`;

const LightboxDefaultAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${DesignSystem.borderRadius.full};
  background: linear-gradient(135deg, ${getColor('accent.500')}, ${getColor('secondary.500')});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.5rem;
`;

const LightboxTitle = styled.h3`
  margin: 0 0 ${getSpacing('md')};
  color: ${getColor('primary.900')};
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1.4;
  font-family: ${DesignSystem.typography.fonts.display};
`;

const LightboxText = styled.p`
  color: ${getColor('neutral.gray.700')};
  font-size: 0.95rem;
  line-height: 1.75;
  margin: 0;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 1.25rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  z-index: 2;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.1);
  }
`;

// Media navigation arrow overlaid on images
const MediaNavButton = styled.button<{ left?: boolean }>`
  position: absolute;
  top: 50%;
  ${p => p.left ? 'left: 8px;' : 'right: 8px;'}
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
  z-index: 3;
  padding: 0;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const MediaChevron = styled.span<{ direction: 'left' | 'right' }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: ${props => props.direction === 'left' ? 'rotate(135deg) translateX(-1px)' : 'rotate(-45deg) translateX(1px)'};
`;

const MediaImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const MediaCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  z-index: 3;
  letter-spacing: 0.05em;
`;

const LightboxMediaWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

// Expand toggle for section header
const ExpandHint = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  color: ${getColor('accent.600')};
  font-weight: 500;
  letter-spacing: 0.08em;
  margin-top: ${getSpacing('xs')};
  cursor: pointer;
  transition: color ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    color: ${getColor('accent.700')};
  }
`;

const ScrollChevron = styled.span<{ direction: 'left' | 'right' }>`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-right: 2.5px solid currentColor;
  border-bottom: 2.5px solid currentColor;
  transform: ${props => props.direction === 'left' ? 'rotate(135deg)' : 'rotate(-45deg)'};
  transition: transform 0.3s ease;
`;

const NavButton = styled.button<{ left?: boolean }>`
  position: absolute;
  top: 50%;
  ${p => p.left ? 'left: -40px;' : 'right: -40px;'}
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${getColor('primary.700')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 10;
  padding: 0;
  
  &:hover {
    color: ${getColor('primary.900')};
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('accent.600')};
    outline-offset: 2px;
  }
  
  @media (max-width: 900px) {
    ${p => p.left ? 'left: -32px;' : 'right: -32px;'}
  }
`;

const NavigationContainer = styled.div`
  position: relative;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${getSpacing('xl')};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 400;
  color: ${getColor('primary.900')};
  margin-bottom: ${getSpacing('sm')};
  font-style: italic;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
  font-family: 'Poppins', 'Montserrat', 'Futura', sans-serif;
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: ${getColor('primary.900')};
  max-width: 700px;
  margin: 0 auto;
  font-style: italic;
  letter-spacing: 0.08em;
  line-height: 1.7;
`;

// Render gradient SVG stars
const renderGradientStars = (rating: number, prefix: string = '') => {
  return (
    <StarsContainer>
      {[1, 2, 3, 4, 5].map(star => (
        <GradientStar key={star} index={star} filled={star <= rating} />
      ))}
    </StarsContainer>
  );
};

// Simple Review Card Component
interface ReviewCardItemProps {
  review: Review;
  onClick: () => void;
}

const ReviewCardItem: React.FC<ReviewCardItemProps> = ({ review, onClick }) => {
  const [imgError, setImgError] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const hasMultipleMedia = review.media_items.length > 1;

  const handleMediaPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMediaIndex(prev => (prev - 1 + review.media_items.length) % review.media_items.length);
  };

  const handleMediaNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMediaIndex(prev => (prev + 1) % review.media_items.length);
  };

  return (
    <ReviewCard onClick={onClick}>
      {review.media_items.length > 0 && (
        <MediaImageWrapper>
          <MediaImage src={review.media_items[mediaIndex].url} alt={review.title} />
          {hasMultipleMedia && (
            <>
              <MediaNavButton left onClick={handleMediaPrev}>
                <MediaChevron direction="left" />
              </MediaNavButton>
              <MediaNavButton onClick={handleMediaNext}>
                <MediaChevron direction="right" />
              </MediaNavButton>
              <MediaCounter>{mediaIndex + 1} / {review.media_items.length}</MediaCounter>
            </>
          )}
        </MediaImageWrapper>
      )}
      <CardContent>
        <ReviewerHeader>
          {review.customer_image && !imgError ? (
            <Avatar
              src={review.customer_image}
              alt={review.customer_name}
              onError={() => setImgError(true)}
            />
          ) : (
            <DefaultAvatar>
              {review.customer_name.charAt(0).toUpperCase()}
            </DefaultAvatar>
          )}
          <ReviewerInfo>
            <ReviewerName>{review.customer_name}</ReviewerName>
            {renderGradientStars(review.rating, review.id)}
          </ReviewerInfo>
        </ReviewerHeader>
        <ReviewTitle>{review.title}</ReviewTitle>
        <ReviewText>{review.review_text}</ReviewText>
      </CardContent>
    </ReviewCard>
  );
};

// Lightbox Component
interface ReviewLightboxProps {
  review: Review | null;
  onClose: () => void;
}

const ReviewLightbox: React.FC<ReviewLightboxProps> = ({ review, onClose }) => {
  const [imgError, setImgError] = useState(false);
  const [mediaIndex, setMediaIndex] = useState(0);
  const hasMultipleMedia = (review?.media_items.length ?? 0) > 1;

  // Reset media index when review changes
  useEffect(() => {
    setMediaIndex(0);
  }, [review?.id]);

  useEffect(() => {
    if (!review) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasMultipleMedia) {
        setMediaIndex(prev => (prev - 1 + review.media_items.length) % review.media_items.length);
      }
      if (e.key === 'ArrowRight' && hasMultipleMedia) {
        setMediaIndex(prev => (prev + 1) % review.media_items.length);
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [review, onClose, hasMultipleMedia]);

  if (!review) return null;

  return (
    <LightboxOverlay $isOpen={!!review} onClick={onClose}>
      <LightboxCard onClick={(e) => e.stopPropagation()}>
        <LightboxClose onClick={onClose} aria-label="Close">×</LightboxClose>
        {review.media_items.length > 0 && (
          <LightboxMediaWrapper>
            <LightboxImage src={review.media_items[mediaIndex].url} alt={review.title} />
            {hasMultipleMedia && (
              <>
                <MediaNavButton left onClick={() => setMediaIndex(prev => (prev - 1 + review.media_items.length) % review.media_items.length)}>
                  <MediaChevron direction="left" />
                </MediaNavButton>
                <MediaNavButton onClick={() => setMediaIndex(prev => (prev + 1) % review.media_items.length)}>
                  <MediaChevron direction="right" />
                </MediaNavButton>
                <MediaCounter>{mediaIndex + 1} / {review.media_items.length}</MediaCounter>
              </>
            )}
          </LightboxMediaWrapper>
        )}
        <LightboxContent>
          <LightboxReviewerHeader>
            {review.customer_image && !imgError ? (
              <LightboxAvatar
                src={review.customer_image}
                alt={review.customer_name}
                onError={() => setImgError(true)}
              />
            ) : (
              <LightboxDefaultAvatar>
                {review.customer_name.charAt(0).toUpperCase()}
              </LightboxDefaultAvatar>
            )}
            <ReviewerInfo>
              <ReviewerName style={{ fontSize: '1.1rem' }}>{review.customer_name}</ReviewerName>
              {renderGradientStars(review.rating, `lb-${review.id}`)}
            </ReviewerInfo>
          </LightboxReviewerHeader>
          <LightboxTitle>{review.title}</LightboxTitle>
          <LightboxText>{review.review_text}</LightboxText>
        </LightboxContent>
      </LightboxCard>
    </LightboxOverlay>
  );
};

export const ReviewsCarouselOption1: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const reviewsPerPage = 5;

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/reviews?limit=20`);
      const data = await response.json();
      const reviewsList = data.data || data.reviews || [];
      setReviews(shuffleArray(reviewsList));
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  useEffect(() => {
    fetchReviews();
    const handler = () => { fetchReviews(); setCurrentPage(0); };
    window.addEventListener('review-submitted', handler);
    return () => window.removeEventListener('review-submitted', handler);
  }, []);

  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const currentReviews = reviews.slice(
    currentPage * reviewsPerPage,
    (currentPage + 1) * reviewsPerPage
  );

  const handlePrevious = () => {
    setCurrentPage(prev => prev === 0 ? totalPages - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentPage(prev => (prev + 1) % totalPages);
  };

  const handleCloseLightbox = useCallback(() => {
    setSelectedReview(null);
  }, []);

  return (
    <Option1Container>
      <CarouselWrapper>
        <SectionHeader>
          <SectionTitle>Customer Stories</SectionTitle>
          <SectionSubtitle>
            Real experiences from travelers who explored the Himalayas with us
          </SectionSubtitle>
        </SectionHeader>

        <NavigationContainer>
          <NavButton left onClick={handlePrevious}>
            <ScrollChevron direction="left" />
          </NavButton>
          
          <ReviewsGrid>
            {currentReviews.map((review) => (
              <ReviewCardItem
                key={review.id}
                review={review}
                onClick={() => setSelectedReview(review)}
              />
            ))}
          </ReviewsGrid>
          
          <NavButton onClick={handleNext}>
            <ScrollChevron direction="right" />
          </NavButton>
        </NavigationContainer>
      </CarouselWrapper>

      <ReviewLightbox review={selectedReview} onClose={handleCloseLightbox} />
    </Option1Container>
  );
};

// ==============================================
// OPTION 2: SLIDING TRAY WITH BUTTONS
// Smooth slide animation, 4 at a time
// ==============================================

const Option2Container = styled.section`
  padding: ${getSpacing('5xl')} 0;
  background: ${getColor('neutral.white')};
`;

const SliderContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
  position: relative;
`;

const SliderViewport = styled.div`
  overflow: hidden;
  margin: ${getSpacing('3xl')} 0;
`;

const SliderTrack = styled.div<{ offset: number }>`
  display: flex;
  gap: ${getSpacing('xl')};
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${props => props.offset}px);
`;

const SlideCard = styled.div`
  min-width: calc((100% - ${getSpacing('xl')} * 3) / 4);
  
  @media (max-width: 1200px) {
    min-width: calc((100% - ${getSpacing('xl')} * 2) / 3);
  }
  
  @media (max-width: 900px) {
    min-width: calc((100% - ${getSpacing('xl')}) / 2);
  }
  
  @media (max-width: 600px) {
    min-width: 100%;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${getSpacing('md')};
  margin-top: ${getSpacing('2xl')};
`;

const SlideButton = styled.button<{ disabled?: boolean }>`
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  border-radius: ${DesignSystem.borderRadius.lg};
  border: none;
  background: ${props => props.disabled 
    ? getColor('neutral.gray.300') 
    : `linear-gradient(135deg, ${getColor('accent.600')}, ${getColor('secondary.600')})`};
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all ${DesignSystem.motion.duration.fast};
  box-shadow: ${getShadow('soft')};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${getShadow('medium')};
  }
`;

const DotIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: ${getSpacing('sm')};
  margin-top: ${getSpacing('lg')};
`;

const Dot = styled.button<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: ${DesignSystem.borderRadius.full};
  border: none;
  background: ${props => props.active ? getColor('accent.600') : getColor('neutral.gray.300')};
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast};
  
  &:hover {
    background: ${props => props.active ? getColor('accent.700') : getColor('accent.400')};
    transform: scale(1.2);
  }
`;

export const ReviewsCarouselOption2: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchReviews();
    updateCardWidth();
    window.addEventListener('resize', updateCardWidth);
    return () => window.removeEventListener('resize', updateCardWidth);
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/reviews?limit=20`);
      const data = await response.json();
      setReviews(data.data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const updateCardWidth = () => {
    if (viewportRef.current) {
      const viewportWidth = viewportRef.current.offsetWidth;
      const gap = 32; // xl spacing
      const width = window.innerWidth;
      
      if (width > 1200) {
        setCardWidth((viewportWidth - gap * 3) / 4 + gap);
      } else if (width > 900) {
        setCardWidth((viewportWidth - gap * 2) / 3 + gap);
      } else if (width > 600) {
        setCardWidth((viewportWidth - gap) / 2 + gap);
      } else {
        setCardWidth(viewportWidth);
      }
    }
  };

  const maxSlide = Math.max(0, reviews.length - 4);
  const offset = -currentSlide * cardWidth;

  const handlePrevious = () => {
    setCurrentSlide(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentSlide(prev => Math.min(maxSlide, prev + 1));
  };

  return (
    <Option2Container>
      <SliderContainer>
        <SectionHeader>
          <SectionTitle>Traveler Testimonials</SectionTitle>
          <SectionSubtitle>
            Discover authentic experiences shared by our adventurers
          </SectionSubtitle>
        </SectionHeader>

        <SliderViewport ref={viewportRef}>
          <SliderTrack offset={offset}>
            {reviews.map((review) => (
              <SlideCard key={review.id}>
                <ReviewCardItem review={review} onClick={() => {}} />
              </SlideCard>
            ))}
          </SliderTrack>
        </SliderViewport>

        <ButtonContainer>
          <SlideButton onClick={handlePrevious} disabled={currentSlide === 0}>
            ← Previous
          </SlideButton>
          <SlideButton onClick={handleNext} disabled={currentSlide >= maxSlide}>
            Next →
          </SlideButton>
        </ButtonContainer>

        <DotIndicators>
          {Array.from({ length: Math.ceil(reviews.length / 4) }).map((_, index) => (
            <Dot
              key={index}
              active={Math.floor(currentSlide / 4) === index}
              onClick={() => setCurrentSlide(index * 4)}
            />
          ))}
        </DotIndicators>
      </SliderContainer>
    </Option2Container>
  );
};

// ==============================================
// OPTION 3: TABS WITH 4-COLUMN GRID
// Tab-based navigation, always 4 per view
// ==============================================

const Option3Container = styled.section`
  padding: ${getSpacing('5xl')} 0;
  background: linear-gradient(135deg, ${getColor('primary.50')}, ${getColor('accent.50')});
`;

const TabContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${getSpacing('xl')};
`;

const TabButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${getSpacing('md')};
  margin: ${getSpacing('3xl')} 0;
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: ${getSpacing('md')} ${getSpacing('xl')};
  border-radius: ${DesignSystem.borderRadius.full};
  border: 2px solid ${props => props.active ? getColor('accent.600') : getColor('neutral.gray.300')};
  background: ${props => props.active ? getColor('accent.600') : getColor('neutral.white')};
  color: ${props => props.active ? 'white' : getColor('primary.900')};
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast};
  
  &:hover {
    border-color: ${getColor('accent.600')};
    background: ${props => props.active ? getColor('accent.700') : getColor('accent.100')};
  }
`;

export const ReviewsCarouselOption3: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const reviewsPerTab = 4;

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/reviews?limit=20`);
      const data = await response.json();
      setReviews(data.data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const totalTabs = Math.ceil(reviews.length / reviewsPerTab);
  const currentReviews = reviews.slice(
    activeTab * reviewsPerTab,
    (activeTab + 1) * reviewsPerTab
  );

  return (
    <Option3Container>
      <TabContainer>
        <SectionHeader>
          <SectionTitle>What Our Guests Say</SectionTitle>
          <SectionSubtitle>
            Browse through stories from our happy travelers
          </SectionSubtitle>
        </SectionHeader>

        <TabButtons>
          {Array.from({ length: totalTabs }).map((_, index) => (
            <TabButton
              key={index}
              active={activeTab === index}
              onClick={() => setActiveTab(index)}
            >
              Reviews {index * reviewsPerTab + 1} - {Math.min((index + 1) * reviewsPerTab, reviews.length)}
            </TabButton>
          ))}
        </TabButtons>

        <ReviewsGrid>
          {currentReviews.map((review) => (
            <ReviewCardItem key={review.id} review={review} onClick={() => {}} />
          ))}
        </ReviewsGrid>
      </TabContainer>
    </Option3Container>
  );
};
